<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import iconChevron from "@core/assets/icons/chevron.svg?raw";
	import type { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { buildProductAlt } from "../seo/buildProductAlt.js";
	import PreloadedPrice from "./PreloadedPrice.svelte";
	import translations from "./ProductB2bCard.translations.json" with { type: "json" };
	import ProductB2bModal from "./ProductB2bModal.svelte";
	import type { ProductForCard } from "./ProductCard.js";
	import ProductCardCartOptions from "./ProductCardCartOptions.svelte";
	import ProductCardMedia from "./ProductCardMedia.svelte";
	import ProductCardMediaFallback from "./ProductCardMediaFallback.svelte";
	import ProductTags from "./ProductTags.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";
	import { getTagsForProductCard } from "./getTagsForProductCard.js";

	const { tenant } = getGlobalTemplateProps();
	const { product, subcategory }: { product: ProductForCard; subcategory: SubcategoryForCustomer } = $props();

	const primaryVariantId = $derived(product.variants[0]?.id);
	const productPrimaryVariant = $derived(primaryVariantId ? getProductOfVariant(product, primaryVariantId) : undefined);
	const mediaSrc = $derived(
		productPrimaryVariant ? getProductPrimaryMedia(productPrimaryVariant, product.attributes) : undefined,
	);

	let openDetailModal = $state(false);

	const heading = $derived(getProductHeadingWithAttributes(tenant.locale, product));
	const alt = $derived(buildProductAlt(product, subcategory, tenant.locale, product.attributes));
	const translate = getTranslate(translations);
</script>

<div class="grid grid-cols-subgrid col-span-full p-2 lg:pr-10 lg:border border-fcrlightgray-400 rounded-3xl">
	<button
		class="flex gap-4 lg:gap-8 items-center text-left"
		onclick={(): void => {
			openDetailModal = true;
		}}
	>
		<div
			class="bg-fcrlightgray-400 w-[33vw] xs:w-[10.25rem] xl:w-50 2xl:w-67 aspect-square flex overflow-hidden flex-col justify-between p-1.5 sm:p-4 rounded-2xl relative grow-0 shrink-0"
		>
			{#if productPrimaryVariant}
				<ProductTags tags={getTagsForProductCard({ product, locale: tenant.locale, subcategory })} />
				{#if mediaSrc}
					<ProductCardMedia {mediaSrc} {alt} />
				{:else}
					<ProductCardMediaFallback />
				{/if}
			{/if}
		</div>
		<div class="flex flex-col gap-1 xl:gap-2 grow shrink overflow-hidden xl:pr-12 2xl:pr-16">
			<div class="font-antipol text-4 md:text-6 text-fcrdarkgray flex items-center gap-2.5">
				<div>
					<Formatted text={heading} />
				</div>
				<Icon icon={iconChevron} class="size-6 -rotate-90" />
			</div>
			<div
				class="text-3/normal md:text-3.5/normal text-fcrgray line-clamp-2 sm:line-clamp-3 xl:line-clamp-4 w-0 min-w-full"
			>
				<Formatted text={product.plainDescription[tenant.locale]} />
			</div>
		</div>
	</button>
	<div class="grid grid-cols-3 xl:grid-cols-subgrid xl:col-span-3 gap-y-2 my-2 justify-start">
		{#each product.variants as variant (variant)}
			{@const productOfVariant = getProductOfVariant(product, variant.id)}
			{@const stockAmount = getProductStockAmount(product.stock, variant, product.attributes)}
			<div
				class="grid grid-cols-[1fr_1fr_2fr] xl:grid-cols-subgrid col-span-full items-center font-medium text-fcrdarkgray sm:gap-2"
			>
				<div
					class={[
						"xl:text-right text-xs min-[400px]:text-sm sm:text-base order-1 pr-2 sm:pr-7 shrink-0 sm:text-nowrap",
						stockAmount === 0 && "line-through",
					]}
				>
					{#if product.variantType !== ProductVariantType.None}
						<Formatted text={variant.label[tenant.locale]} />
					{/if}
				</div>
				<div class="w-42.5 flex items-center order-3 xl:order-2 ml-auto pl-4">
					{#if stockAmount === 0}
						<div class="w-full text-center text-fcrgray">
							<Formatted text={translate("soldOut")} />
						</div>
					{:else}
						<ProductCardCartOptions
							showZeroCount
							showToasts
							shrinkOnMobile
							product={productOfVariant}
							attributes={product.attributes}
							max={stockAmount}
						/>
					{/if}
				</div>
				<div
					class={[
						"text-right text-xs min-[400px]:text-sm sm:text-base order-2 xl:order-3",
						stockAmount === 0 && "line-through",
					]}
				>
					<PreloadedPrice class="h-6">
						{#snippet children(currency: Currency, destinationCountry: Country | "outsideEu")}
							{@const productPrice = getPriceForCustomerOfProductVariant(
								productOfVariant,
								currency,
								destinationCountry,
								EconomicSubjectVatType.NotVatSubject,
							).withTax}
							{#if currency === Currency.CZK}
								<Formatted text={formatPrice(Math.ceil(productPrice), currency, tenant.locale, false)} />
							{:else}
								<Formatted text={formatPrice(productPrice, currency, tenant.locale, true)} />
							{/if}
						{/snippet}
					</PreloadedPrice>
				</div>
			</div>
		{/each}
	</div>
</div>

{#if productPrimaryVariant}
	<ProductB2bModal {product} {productPrimaryVariant} {subcategory} bind:show={openDetailModal} />
{/if}
